'use client';

import React, { useRef, useCallback } from 'react';

interface MaskedInputProps {
  mask: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  hasError?: boolean;
  id?: string;
  'aria-label'?: string;
  disabled?: boolean;
  /** When true, allows letters as well as digits (for alphanumeric CNPJ) */
  alphanumeric?: boolean;
}

/**
 * Lightweight input masking without external library dependency issues.
 * Mask characters: '9' = digit only, 'a' = letter only, '*' = alphanumeric
 * When alphanumeric=true, '9' slots also accept letters (for new CNPJ format).
 */
function applyMask(raw: string, mask: string, alphanumeric = false): string {
  // Strip formatting chars but keep alphanumeric content
  const chars = alphanumeric
    ? raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase()
    : raw.replace(/\D/g, '');

  let result = '';
  let charIdx = 0;

  for (let i = 0; i < mask.length; i++) {
    if (charIdx >= chars.length) break;
    const maskChar = mask[i];
    if (maskChar === '9' || maskChar === 'a' || maskChar === '*') {
      result += chars[charIdx];
      charIdx++;
    } else {
      result += maskChar;
    }
  }
  return result;
}

export default function MaskedInput({
  mask,
  placeholder,
  value,
  onChange,
  onBlur,
  hasError,
  id,
  'aria-label': ariaLabel,
  disabled,
  alphanumeric = false,
}: MaskedInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const masked = applyMask(e.target.value, mask, alphanumeric);
      onChange(masked);
    },
    [mask, onChange, alphanumeric]
  );

  return (
    <input
      ref={inputRef}
      id={id}
      type="text"
      inputMode={alphanumeric ? 'text' : 'numeric'}
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      onBlur={onBlur}
      disabled={disabled}
      aria-label={ariaLabel}
      className={`input-base ${hasError ? 'input-error' : ''}`}
      autoComplete="off"
      style={{ textTransform: alphanumeric ? 'uppercase' : undefined }}
    />
  );
}